// 나의 프로필 (구인 풀 정보 수정)
import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Flex,
  Grid,
  Text,
  useColorModeValue,
  FormControl,
  FormLabel,
  Input,
  Select,
  Checkbox,
  CheckboxGroup,
  Stack,
  useToast,
  Heading,
  Divider,
  Tag,
  TagLabel,
  TagCloseButton,
  HStack
} from '@chakra-ui/react';
import Card from 'components/Card/Card';
import CardHeader from 'components/Card/CardHeader';
import CardBody from 'components/Card/CardBody';
import { useAuth } from 'contexts/AuthContext';
import { updateUserProfile } from 'utils/firestoreUtils';
import jobTypes from 'constants/jobTypes';

function MyProfile() {
  const { currentUser, userProfile, fetchUserProfile } = useAuth();
  const toast = useToast();
  const textColor = useColorModeValue('gray.700', 'white');

  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    userType: '',
    preferredJobs: [],
    availableDays: [],
    availableTimeSlots: [],
    notificationConsent: false
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (userProfile) {
      setFormData({
        name: userProfile.name || '',
        phone: userProfile.phone || '',
        email: userProfile.email || '',
        userType: userProfile.userType || '',
        preferredJobs: userProfile.preferredJobs || [],
        availableDays: userProfile.availableDays || [],
        availableTimeSlots: userProfile.availableTimeSlots || [],
        notificationConsent: userProfile.notificationConsent || false
      });
    }
  }, [userProfile]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      await updateUserProfile(currentUser.uid, formData);
      await fetchUserProfile(currentUser.uid);
      
      toast({
        title: '저장 완료',
        description: '프로필이 성공적으로 업데이트되었습니다.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('프로필 업데이트 오류:', error);
      toast({
        title: '오류',
        description: '프로필 업데이트에 실패했습니다.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const days = ['월', '화', '수', '목', '금', '토', '일'];
  const timeSlots = ['09-13', '13-17', '17-21', '21-24'];

  const getRoleText = (role) => {
    switch (role) {
      case 'candidate': return '일반 이용자';
      case 'manager': return '매니저';
      case 'admin': return '관리자';
      default: return role;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'active': return '활동중';
      case 'inactive': return '휴면';
      case 'blacklisted': return '블랙리스트';
      default: return status;
    }
  };

  return (
    <Flex direction="column" pt={{ base: '120px', md: '75px' }}>
      <Grid templateColumns={{ sm: '1fr', lg: '2fr 1fr' }} gap={6}>
        {/* 프로필 편집 */}
        <Card>
          <CardHeader p="6px 0px 22px 0px">
            <Text fontSize="xl" color={textColor} fontWeight="bold">
              프로필 정보 수정
            </Text>
          </CardHeader>

          <CardBody>
            <form onSubmit={handleSubmit}>
              <Stack spacing={4}>
                <FormControl isRequired>
                  <FormLabel>이름</FormLabel>
                  <Input
                    value={formData.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    placeholder="홍길동"
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>전화번호</FormLabel>
                  <Input
                    value={formData.phone}
                    onChange={(e) => handleChange('phone', e.target.value)}
                    placeholder="010-1234-5678"
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>이메일</FormLabel>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    placeholder="email@example.com"
                    isDisabled
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>사용자 유형</FormLabel>
                  <Select
                    value={formData.userType}
                    onChange={(e) => handleChange('userType', e.target.value)}
                  >
                    <option value="">선택하세요</option>
                    <option value="학생">학생</option>
                    <option value="기타">기타</option>
                  </Select>
                </FormControl>

                <Divider my={2} />

                <FormControl>
                  <FormLabel>희망 근무 유형</FormLabel>
                  <CheckboxGroup
                    value={formData.preferredJobs}
                    onChange={(values) => handleChange('preferredJobs', values)}
                  >
                    <Stack spacing={2} direction="row" flexWrap="wrap">
                      {jobTypes.map(type => (
                        <Checkbox key={type} value={type}>{type}</Checkbox>
                      ))}
                    </Stack>
                  </CheckboxGroup>
                </FormControl>

                <FormControl>
                  <FormLabel>근무 가능 요일</FormLabel>
                  <CheckboxGroup
                    value={formData.availableDays}
                    onChange={(values) => handleChange('availableDays', values)}
                  >
                    <Stack spacing={2} direction="row" flexWrap="wrap">
                      {days.map(day => (
                        <Checkbox key={day} value={day}>{day}</Checkbox>
                      ))}
                    </Stack>
                  </CheckboxGroup>
                </FormControl>

                <FormControl>
                  <FormLabel>근무 가능 시간대</FormLabel>
                  <CheckboxGroup
                    value={formData.availableTimeSlots}
                    onChange={(values) => handleChange('availableTimeSlots', values)}
                  >
                    <Stack spacing={2} direction="row" flexWrap="wrap">
                      {timeSlots.map(slot => (
                        <Checkbox key={slot} value={slot}>{slot}시</Checkbox>
                      ))}
                    </Stack>
                  </CheckboxGroup>
                </FormControl>

                <Divider my={2} />

                <FormControl>
                  <Box
                    bg="gray.50"
                    p="16px"
                    borderRadius="8px"
                  >
                    <Checkbox
                      isChecked={formData.notificationConsent}
                      onChange={(e) => handleChange('notificationConsent', e.target.checked)}
                      colorScheme="teal"
                      fontWeight="bold"
                      mb="8px"
                    >
                      구인·구직 알림톡/문자/이메일 수신 동의
                    </Checkbox>
                    <Text fontSize="xs" color="gray.600" ml="24px">
                      구인 공고, 근무 요청, 행사 인력 모집 등의 안내를 위해 카카오 알림톡, 문자, 이메일 등으로 메시지를 수신합니다.
                      미동의 시 일부 구인 공고 알림 및 개별 안내 수신이 제한될 수 있습니다.
                    </Text>
                  </Box>
                </FormControl>

                <Button
                  type="submit"
                  colorScheme="blue"
                  isLoading={loading}
                  loadingText="저장 중..."
                  mt={4}
                >
                  저장하기
                </Button>
              </Stack>
            </form>
          </CardBody>
        </Card>

        {/* 계정 정보 */}
        <Stack spacing={6}>
          <Card>
            <CardHeader p="6px 0px 22px 0px">
              <Text fontSize="xl" color={textColor} fontWeight="bold">
                계정 정보
              </Text>
            </CardHeader>

            <CardBody>
              <Stack spacing={3}>
                <Box>
                  <Text fontSize="sm" color="gray.500">역할</Text>
                  <Text fontWeight="bold">{getRoleText(userProfile?.role)}</Text>
                </Box>
                
                <Box>
                  <Text fontSize="sm" color="gray.500">상태</Text>
                  <Text fontWeight="bold">{getStatusText(userProfile?.status)}</Text>
                </Box>

                <Box>
                  <Text fontSize="sm" color="gray.500">가입일</Text>
                  <Text fontSize="sm">
                    {userProfile?.createdAt 
                      ? new Date(userProfile.createdAt).toLocaleDateString()
                      : '-'
                    }
                  </Text>
                </Box>
              </Stack>
            </CardBody>
          </Card>

          <Card>
            <CardHeader p="6px 0px 22px 0px">
              <Text fontSize="xl" color={textColor} fontWeight="bold">
                구인 풀 참여 현황
              </Text>
            </CardHeader>

            <CardBody>
              <Stack spacing={3}>
                <Box>
                  <Text fontSize="sm" color="gray.500" mb={2}>희망 업무</Text>
                  <HStack spacing={2} flexWrap="wrap">
                    {userProfile?.preferredJobs?.length > 0 ? (
                      userProfile.preferredJobs.map(job => (
                        <Tag key={job} colorScheme="blue" size="sm">
                          <TagLabel>{job}</TagLabel>
                        </Tag>
                      ))
                    ) : (
                      <Text fontSize="sm" color="gray.400">설정 안됨</Text>
                    )}
                  </HStack>
                </Box>

                <Box>
                  <Text fontSize="sm" color="gray.500" mb={2}>가능 요일</Text>
                  <HStack spacing={2} flexWrap="wrap">
                    {userProfile?.availableDays?.length > 0 ? (
                      userProfile.availableDays.map(day => (
                        <Tag key={day} colorScheme="green" size="sm">
                          <TagLabel>{day}</TagLabel>
                        </Tag>
                      ))
                    ) : (
                      <Text fontSize="sm" color="gray.400">설정 안됨</Text>
                    )}
                  </HStack>
                </Box>

                <Box>
                  <Text fontSize="sm" color="gray.500" mb={2}>가능 시간대</Text>
                  <HStack spacing={2} flexWrap="wrap">
                    {userProfile?.availableTimeSlots?.length > 0 ? (
                      userProfile.availableTimeSlots.map(slot => (
                        <Tag key={slot} colorScheme="purple" size="sm">
                          <TagLabel>{slot}시</TagLabel>
                        </Tag>
                      ))
                    ) : (
                      <Text fontSize="sm" color="gray.400">설정 안됨</Text>
                    )}
                  </HStack>
                </Box>
              </Stack>
            </CardBody>
          </Card>
        </Stack>
      </Grid>
    </Flex>
  );
}

export default MyProfile;

